import { ChangeEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import SweetAlertService from "../../../services/SweetAlertService";
import EmployerService from "../Services/EmployerService";
import { AppConstants, employerListUrl, errorMessages, getRequiredErrorMessageForCountry, namePattern } from "../Shared/Constants/AppConstant";
import { SubRoleType } from "../../../utils/SubRoleConstants";
import './Employer.css';

interface countryDetails {
    countryId: any;
    value: any;
    text: any;
    sessionLengths?: Array<{
        subRoleId: number;
        sessionLength: string;
    }>;
}
export const AddEmployer = () => {
    const navigate = useNavigate();
    const { register, handleSubmit, setValue, getValues, watch, reset, formState: { errors } }
        = useForm({ defaultValues: { hoursPerWeek: 0, minutesPerWeek: 0, sessionsPerWeek: 0, countryId: 0, employerName: "", subRoleId: 0 } });

    const [countries, setCountries] = useState<countryDetails[]>([]);
    const [employmentType, setEmploymentType] = useState([]);
    const [subRoles, setSubRoles] = useState([]);
    const [selectedSubRoleId, setSelectedSubRoleId] = useState<number>(0);
    const [showContractedHours, setShowContractedHours] = useState<boolean>(true);
    const [inputValue, setInputValue] = useState("0");
    const [inputRoundedValue, setInputRoundedValue] = useState<any>();

    useEffect(() => {
        LoadEmploymentDetails();
    }, []);

    //To populate employer details
    const LoadEmploymentDetails = () => {
        EmployerService.loadEmployer().then((response) => {
            if (response.data) {
                setCountries(response.data.countries);
                setEmploymentType(response.data.employmentType);
                setSubRoles(response.data.subRoles);
                setValue("employerName", "Workplace " + (Number(response.data.employerNames.length) + 1));
            }
        })
            .catch(() => {
                SweetAlertService.error("Error", "Something went wrong while getting workplace details");
            });
    }

    //For input field allowing only two digits 
    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const enteredValue = event.target.value;
        const sanitizedValue = enteredValue.slice(0, 2); // Extract the first two characters
        setInputValue(sanitizedValue);
    };

    //To allow only two decimal
    const allowTwoDecimals = (event: ChangeEvent<HTMLInputElement>) => {
        const enteredValue = event.target.value;
        const sanitizedValue = enteredValue.replace(/[^0-9.]/g, '');
        const decimalParts = sanitizedValue.split('.');
        const integerPart = decimalParts[0];
        const decimalPart = decimalParts[1] ? decimalParts[1].slice(0, 2) : '';
        const roundedValue = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
        setInputRoundedValue(roundedValue);
    }

    //Handle SubRole change and update contracted hours visibility
    const handleSubRoleChange = (subRoleId: number) => {
        setSelectedSubRoleId(subRoleId);
        const shouldShowContractedHours = subRoleId === SubRoleType.SalariedGP || subRoleId === SubRoleType.Academic;
        setShowContractedHours(shouldShowContractedHours);
        // Reset contracted hours values if hiding the section
        if (!shouldShowContractedHours) {
            setValue("hoursPerWeek", 0);
            setValue("minutesPerWeek", 0);
            setValue("sessionsPerWeek", 0);
            setInputValue("0");
            setInputRoundedValue("0");
        } else {
            // Recalculate sessions based on new SubRole if country is already selected
            const selectedCountry = Number(getValues("countryId"));
            if (selectedCountry > 0) {
                CalculateSession({ name: "subRoleId" });
            }
        }
    };

    //To calculate session and hours, mins based on country and SubRole
    const CalculateSession = (e: any) => {
        var selectedCountry = Number(getValues("countryId"));
        var selectedSubRoleId = Number(getValues("subRoleId"));
        let getCountryDetails = countries.filter(x => x.countryId == selectedCountry)[0];
        if (getCountryDetails && selectedCountry > 0 && (selectedSubRoleId == SubRoleType.SalariedGP || selectedSubRoleId == SubRoleType.Academic)) {
            // Get session length based on selected SubRole and Country
            var sessionLength = AppConstants.Default_Session_Length;
            if (getCountryDetails.sessionLengths) {
                const subRoleSessionLength = getCountryDetails?.sessionLengths.find(x => x.subRoleId === selectedSubRoleId);
                sessionLength = subRoleSessionLength?.sessionLength ?? AppConstants.Default_Session_Length;
            }

            let inputHrsPerWeek = getValues("hoursPerWeek");
            let inputMinsPerWeek = getValues("minutesPerWeek");
            let inputSessionPerWeek = getValues("sessionsPerWeek");
            const [hh, mm] = sessionLength.split(':');

            //Calculation if input value greater than zero
            if (Number(inputHrsPerWeek) >= 0 && Number(inputMinsPerWeek) >= 0 && Number(inputSessionPerWeek) >= 0) {
                var totalminutes = Number(hh) * 60 + Number(mm);
                var totalminutestoset = Number(inputHrsPerWeek) * 60 + Number(inputMinsPerWeek);
                if (e.name == "sessionsPerWeek") {
                    //set Hours and Minutes
                    var minutestoset = Number(inputSessionPerWeek) * totalminutes;
                    const hours = Math.floor(minutestoset / 60);
                    const minutes = Math.floor(minutestoset % 60);
                    setValue("hoursPerWeek", hours);
                    setValue("minutesPerWeek", minutes);
                }
                else if (e.name == "countryId" || e.name == "subRoleId") {
                    if (inputSessionPerWeek > 0) {
                        //set Hours and Minutes
                        var minutestoset = Number(inputSessionPerWeek) * totalminutes;
                        const hours = Math.floor(minutestoset / 60);
                        const minutes = Math.floor(minutestoset % 60);
                        setValue("hoursPerWeek", hours);
                        setValue("minutesPerWeek", minutes);
                    }
                    else {
                        //set Session
                        var session = (totalminutestoset / totalminutes).toFixed(2);
                        setValue("sessionsPerWeek", Number(session))
                    }
                }
                else {
                    //set Session
                    var session = (totalminutestoset / totalminutes).toFixed(2);
                    setValue("sessionsPerWeek", Number(session))
                }
            }
        }
    }

    //To check whether hours, minutes and sessions are with in specified range
    const isValid = (data: any) => {
        let errorMessage = "";
        let isValid = true;

        // Range validation for contracted hours (when provided)
        if (data.hoursPerWeek > 0 || data.minutesPerWeek > 0 || data.sessionsPerWeek > 0) {
            if (data.hoursPerWeek > AppConstants.Max_Hours_Per_Week || data.hoursPerWeek < 0) {
                isValid = false;
                errorMessage += errorMessages.hoursPatternRange + "<br/>";
            }
            if (data.minutesPerWeek > AppConstants.Max_Minutes_Per_Week || data.minutesPerWeek < 0) {
                isValid = false;
                errorMessage += errorMessages.minutesPatternRange + "<br/>";
            }
            if (data.sessionsPerWeek > AppConstants.Max_Session_Per_Week || data.sessionsPerWeek < 0) {
                isValid = false;
                errorMessage += errorMessages.sessionPatternRange + "<br/>";
            }
        }
        if (!isValid) {
            SweetAlertService.errorWithHTML(AppConstants.SweetAlert_Error_Title, errorMessage);
        }
        return isValid;
    }

    const onSubmit = (data: any) => {
        data.employerName = data.employerName.substr(0, 20);
        data.countryId = Number(data.countryId);
        data.subRoleId = Number(data.subRoleId);
        // Set contracted hours to 0 if not applicable for this SubRole
        if (!showContractedHours) {
            data.sessionsPerWeek = 0;
            data.minutesPerWeek = 0;
            data.hoursPerWeek = 0;
        } else {
            data.sessionsPerWeek = Number(data.sessionsPerWeek) ?? 0.0;
            data.minutesPerWeek = Number(data.minutesPerWeek) ?? 0;
            data.hoursPerWeek = Number(data.hoursPerWeek) ?? 0;
        }
        if (isValid(data)) {
            EmployerService.addEmployer(data).then((response) => {
                if (response.data > 0) {
                    reset();
                    navigate(employerListUrl);
                    window.location.reload();
                } else {
                    SweetAlertService.error("Error", "Something went wrong while adding workplace");
                }
            }).catch(error => {
                if (error.response.data.errors.EmployerName) {
                    SweetAlertService.error("Error", error.response.data.errors.EmployerName[0])
                }
                else {
                    SweetAlertService.error("Error", "Error while saving workplace")
                }
            })
        }
    };

    return (
        <>
            <main>
                <div className="hero-banner hero-banner--background hero-banner--subpage">
                    <div className="container">
                        <div className="hero-banner__image-container">
                            <div className="my-4">
                                <h1>Add workplace details</h1>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className="form-group">
                                        <div className="front-start">
                                            <p className="fw-bold">How do you want to refer to your workplace in the diary?</p>
                                            <p> You can use an abbreviation of your workplace/practice name, a date or details of your role. i.e. NLHC 2022.<br />
                                                This will be used throughout the diary, so try to make this as short as possible.
                                            </p>
                                            <div className="front-start col-sm-3 ps-0">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    {...register("employerName", {
                                                        pattern: {
                                                            value: namePattern,
                                                            message: errorMessages.pattern
                                                        }
                                                    })}
                                                />
                                                {errors.employerName && <span className="help-block m-b-none text-danger field-validation-valid">{errors.employerName.message?.toString()}</span>}
                                            </div>
                                            <br />
                                            <div className="form-group ">
                                                <label className="front-start mb-2"><b>Nation</b>
                                                    <b className="text-danger ">*</b>
                                                </label>
                                                <div className="front-start col-sm-3 mb-4 ps-0">
                                                    <select className="form-select"
                                                        {...register("countryId", {
                                                            required: getRequiredErrorMessageForCountry("nation"),
                                                            onChange: (e) => CalculateSession(e.target),
                                                        })}>
                                                        <option value="">Select</option>
                                                        {countries.length > 0 &&
                                                            countries.map((e: any) => (
                                                                <option value={e.value} key={e.value}>{e.text}</option>
                                                            ))
                                                        }
                                                    </select>
                                                    {errors.countryId && <span className="help-block m-b-none text-danger field-validation-valid">{errors.countryId.message?.toString()}</span>}
                                                </div>
                                            </div>
                                            <div className="form-group ">
                                                <label className="front-start mb-2"><b>Role Type</b>
                                                    <b className="text-danger ">*</b>
                                                </label>
                                                <div className="front-start col-sm-3 mb-4 ps-0">
                                                    <select className="form-select"
                                                        {...register("subRoleId", {
                                                            required: getRequiredErrorMessageForCountry("Role"),
                                                            onChange: (e) => handleSubRoleChange(Number(e?.target?.value)),
                                                        })}>
                                                        <option value="">Select</option>
                                                        {subRoles?.length > 0 &&
                                                            subRoles.map((subRole: any) => (
                                                                <option value={subRole?.id} key={subRole?.id}>{subRole?.name}</option>
                                                            ))
                                                        }
                                                    </select>
                                                    {errors?.subRoleId && <span className="help-block m-b-none text-danger field-validation-valid">{errors?.subRoleId?.message?.toString()}</span>}
                                                </div>
                                            </div>
                                            {showContractedHours && (
                                                <>
                                                    {selectedSubRoleId === SubRoleType.SalariedGP && (
                                                        <p>If you are a salaried GP, the current BMA recommendation for a session of GP care is 4 hours 10 minutes. Enter your contracted hours as time/week if your contracted sessions do not reflect this.</p>
                                                    )}
                                                    <p>You may choose to leave this section blank if your role doesn't involve contracted hours.</p>
                                                </>
                                            )}
                                            {showContractedHours && (
                                                <div className="table-responsive-xl front-start">
                                                    <table className="table customTable mb-4">
                                                        <thead>
                                                            <tr className="d-flex flex-row">
                                                                <th className="col-sm-7 customHeader"></th>
                                                                <th className="col-sm-3 customHeader">Time/week</th>
                                                                <th className="col-sm-2 customHeader">Sessions/week</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr className="d-flex flex-row ">
                                                                <td className="col-sm-7 py-4">{employmentType}</td>
                                                                <td className="d-flex flex-row col-sm-3">
                                                                    <input type="number" className="form-control col-xs-3"
                                                                        {...register("hoursPerWeek", {
                                                                            onBlur: (e) => CalculateSession(e.target),
                                                                        })}
                                                                    />
                                                                    <label className="col-sm-1 mt-2">&nbsp;hrs </label>
                                                                    <input type="number" className="form-control col-xs-3 ms-4" value={inputValue}
                                                                        onInput={handleInputChange}
                                                                        {...register("minutesPerWeek", {
                                                                            onBlur: (e) => CalculateSession(e.target),
                                                                        })}
                                                                    />
                                                                    <label className="col-sm-1 mt-2">&nbsp;mins</label>
                                                                </td>
                                                                <td className="d-flex flex-row col-sm-2">
                                                                    <input type="number" className="form-control col-xs-6 ms-3" step={0.01} value={inputRoundedValue}
                                                                        onInput={allowTwoDecimals}
                                                                        {...register("sessionsPerWeek", {
                                                                            onBlur: (e) => CalculateSession(e.target),
                                                                        })}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                            <div className="front-start">
                                                <button className="btn btn-primary" type="submit">Submit</button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    )
}